/**
 * OS DIZIMISTAS de uma congregação no mês, só leitura (fase 6 do espelho, 19/10/2026).
 *
 * A marcação é a da "Relação de dizimistas" do notebook (`RelatorioDizimistas.tsx`): uma linha por
 * pessoa, com dízimos, votos, total e quantos lançamentos; dízimo ou voto zerado fica em branco; o
 * total do mês no pé. O que o painel acrescenta é a BUSCA pelo nome, só na tela: com a lista
 * filtrada, o pé continua sendo o total do mês, e a linha de cima diz quantas pessoas aparecem.
 *
 * Quem não vê a folha de dízimos desta congregação nem chega aqui: o servidor recusa, e a tela de
 * Congregação não oferece o caminho.
 */
import { useState, type JSX } from 'react'
import { servidor, type Eu, type RelacaoDeDizimistas } from '../api'
import { AtualizadoAs } from '../componentes/AtualizadoAs'
import { BarraDeCima } from '../componentes/BarraDeCima'
import { NavegarMes, useTelaDoMes } from '../componentes/TelaDoMes'
import { emReais } from '../dinheiro'
import { ehParaImprimir, enderecoDaCongregacao, enderecoDaTelaDaCongregacao, irPara } from '../rotas'

/** Sem acento e sem diferença de maiúscula: "joao" acha "João". */
function paraProcurar(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()
}

function pessoas(n: number): string {
  return n === 1 ? '1 pessoa' : `${n} pessoas`
}

export function Dizimistas({
  eu,
  id,
  competenciaDoEndereco,
  aoSair
}: {
  eu: Eu
  id: string
  competenciaDoEndereco: string | null
  aoSair: () => void
}): JSX.Element {
  const paraImprimir = ehParaImprimir()
  const { dados, erro, atualizacao } = useTelaDoMes<RelacaoDeDizimistas>(
    `${id}|${competenciaDoEndereco ?? ''}`,
    () => servidor.dizimistas(id, competenciaDoEndereco),
    aoSair,
    paraImprimir
  )
  const [procura, setProcura] = useState('')

  const mes = dados?.competencia ?? competenciaDoEndereco ?? null
  const andar = (para: string): void => irPara(enderecoDaTelaDaCongregacao('dizimistas', id, para))
  const filtro = paraProcurar(procura)
  const visiveis = dados === null
    ? []
    : filtro === '' ? dados.pessoas : dados.pessoas.filter((p) => paraProcurar(p.nome).includes(filtro))

  return (
    <>
      {!paraImprimir && (
        <BarraDeCima eu={eu} aoSair={aoSair} voltarPara={{ endereco: enderecoDaCongregacao(id, mes), texto: 'Folhas' }} />
      )}

      <main className="folha folha-lancamento tela-dos-dizimistas" data-pronto={dados !== null ? 'sim' : erro !== null ? 'erro' : 'nao'}>
        {erro !== null && <p className="recado" role="alert">{erro}</p>}
        {dados === null && erro === null && <p className="recado aguarde">Carregando...</p>}

        {dados !== null && (
          <>
            {!paraImprimir && <AtualizadoAs atualizacao={atualizacao} />}
            {mes !== null && !paraImprimir && (
              <NavegarMes mes={mes} andar={andar}>
                {dados.pode_baixar_o_pdf && (
                  <a className="baixar" href={`/v1/painel/congregacoes/${encodeURIComponent(id)}/dizimistas.pdf?competencia=${mes}`}>
                    Baixar em PDF
                  </a>
                )}
              </NavegarMes>
            )}

            <h1>RELAÇÃO DE DIZIMISTAS – {dados.competencia_por_extenso.toUpperCase()}</h1>
            <p className="explicacao centro">
              {dados.congregacao.nome} · {pessoas(dados.pessoas.length)}
            </p>

            {!paraImprimir && dados.pessoas.length > 0 && (
              <label className="procurar-nome so-tela">
                Procurar pelo nome
                <input
                  type="search"
                  value={procura}
                  onChange={(e) => setProcura(e.target.value)}
                  placeholder="Digite parte do nome"
                  autoComplete="off"
                />
              </label>
            )}
            {filtro !== '' && (
              <p className="explicacao so-tela" role="status">
                {visiveis.length === 0
                  ? 'Ninguém com esse nome neste mês.'
                  : `${pessoas(visiveis.length)} de ${dados.pessoas.length}. O total do pé é o do mês inteiro.`}
              </p>
            )}

            <div className="rola-a-tabela">
            <p className="rola-para-o-lado so-tela">Arraste a folha para o lado para ver todas as colunas.</p>
            <table className="grade relacao-de-dizimistas">
              <colgroup>
                <col /><col style={{ width: '18%' }} /><col style={{ width: '18%' }} />
                <col style={{ width: '18%' }} /><col style={{ width: '9%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="rotulo">NOME</th>
                  <th className="rotulo valor">DÍZIMOS</th>
                  <th className="rotulo valor">VOTOS</th>
                  <th className="rotulo valor">TOTAL</th>
                  <th className="rotulo valor">LANÇ.</th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map((p) => (
                  <tr key={p.membro_id}>
                    <td className="impresso">{p.nome}</td>
                    <td className="valor manuscrito">{p.dizimos > 0 ? emReais(p.dizimos) : ''}</td>
                    <td className="valor manuscrito">{p.votos > 0 ? emReais(p.votos) : ''}</td>
                    <td className="valor manuscrito">{emReais(p.total)}</td>
                    <td className="valor impresso">{p.lancamentos}</td>
                  </tr>
                ))}
                {dados.pessoas.length === 0 && (
                  <tr><td className="impresso" colSpan={5}>Nenhum dízimo ou voto lançado neste mês.</td></tr>
                )}
                <tr className="rodape">
                  <td>TOTAL</td>
                  <td className="valor total">{emReais(dados.total_dizimos)}</td>
                  <td className="valor total">{emReais(dados.total_votos)}</td>
                  <td className="valor total">{emReais(dados.total)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
            </div>
          </>
        )}
      </main>
    </>
  )
}
